
import Vue from "vue";
import WorkspaceActionCards from "@/components/WorkspaceActionCards.vue";

type EmptySelectionMeta = {
  noun: string;
  nounWithArticle: string;
};

type OrbitNode = {
  colorClass: string;
  iconClass: string;
  key: string;
  position: string;
};

type AmbientNode = {
  colorClass: string;
  iconClass: string;
  key: string;
  orbitClass: string;
  style: Record<string, string>;
};

type MatrixTile = {
  key: string;
  style: Record<string, string>;
};

type MatrixThread = {
  key: string;
  style: Record<string, string>;
};

type MatrixNode = {
  key: string;
  style: Record<string, string>;
};

export default Vue.extend({
  components: {
    WorkspaceActionCards,
  },

  props: {
    selectedTab: {
      type: String,
      default: "recipes",
    },
  },

  data() {
    return {
      isMatrixIntroVisible: true,
      matrixIntroTimerId: null as number | null,
    };
  },

  mounted() {
    this.matrixIntroTimerId = window.setTimeout(() => {
      this.isMatrixIntroVisible = false;
      this.matrixIntroTimerId = null;
    }, 1450);
  },

  beforeDestroy() {
    if (this.matrixIntroTimerId !== null) {
      window.clearTimeout(this.matrixIntroTimerId);
      this.matrixIntroTimerId = null;
    }
  },

  methods: {
    handleSourceAction(key: string) {
      this.$emit("select-tab", key);
    },

    t(key: string, fallback: string, ...args: Array<string | number>) {
      const translation = (this.$store.state as any)?.LocalizationModule?.translations?.get?.(key, ...args);
      return translation && translation !== "---" ? translation : fallback;
    },
  },

  computed: {
    emptyStateTitleId(): string {
      return "allergen-empty-state-title";
    },

    translations(): any {
      return (this.$store.state as any)?.LocalizationModule?.translations;
    },

    selectionMeta(): EmptySelectionMeta {
      if (this.selectedTab === "combinedRecipes") {
        return {
          noun: "combined recipe",
          nounWithArticle: "a combined recipe",
        };
      }

      if (this.selectedTab === "customRecipes") {
        return {
          noun: "custom ingredient",
          nounWithArticle: "a custom ingredient",
        };
      }

      return {
        noun: "recipe",
        nounWithArticle: "a recipe",
      };
    },

    selectionNoun(): string {
      return this.selectionMeta.noun;
    },

    selectionNounWithArticle(): string {
      return this.selectionMeta.nounWithArticle;
    },

    emptyTitle(): string {
      if (this.selectedTab === "combinedRecipes") {
        return this.t("allergens_ai_empty_title_combined_recipe", "No combined recipe selected");
      }

      if (this.selectedTab === "customRecipes") {
        return this.t("allergens_ai_empty_title_custom_ingredient", "No custom ingredient selected");
      }

      return this.t("allergens_ai_empty_title_recipe", "No recipe selected");
    },

    emptyMessage(): string {
      return "";
    },

    sidebarHint(): string {
      return this.t(
        "allergens_ai_empty_pick_sidebar_selection",
        `Select a ${this.selectionNoun} from the sidebar to analyze`,
        this.selectionNoun
      );
    },

    sourceCards() {
      return [
        {
          key: "recipes",
          title: this.t("allergens_ai_item_type_recipe", "Recipe"),
          icon: "fa fa-book",
          tone: "recipe",
          description: this.t("allergens_ai_empty_card_recipe", "Analyze Recipe Allergens"),
        },
        {
          key: "combinedRecipes",
          title: this.t("allergens_ai_item_type_combined_recipe", "Combined Recipe"),
          icon: "fa fa-cutlery",
          tone: "combined",
          description: this.t("allergens_ai_empty_card_combined_recipe", "Analyze Combined Recipe Allergens"),
        },
        {
          key: "customRecipes",
          title: this.t("allergens_ai_item_type_custom_ingredient", "Custom Ingredient"),
          icon: "fa fa-spoon",
          tone: "ingredient",
          description: this.t("allergens_ai_empty_card_custom_ingredient", "Analyze Custom Ingredient Allergens"),
        },
      ];
    },

    orbitNodes(): OrbitNode[] {
      return [
        {
          key: "milk",
          colorClass: "milk",
          iconClass: "fa fa-tint",
          position: "top",
        },
        {
          key: "egg",
          colorClass: "egg",
          iconClass: "fa fa-circle-o",
          position: "top-right",
        },
        {
          key: "nuts",
          colorClass: "nuts",
          iconClass: "fa fa-circle",
          position: "right",
        },
        {
          key: "soy",
          colorClass: "soy",
          iconClass: "fa fa-leaf",
          position: "bottom",
        },
        {
          key: "gluten",
          colorClass: "gluten",
          iconClass: "fa fa-pagelines",
          position: "left",
        },
        {
          key: "sesame",
          colorClass: "sesame",
          iconClass: "fa fa-sun-o",
          position: "top-left",
        },
      ];
    },

    ambientNodes(): AmbientNode[] {
      return [
        {
          key: "sesame",
          orbitClass: "upper-left",
          colorClass: "sesame",
          iconClass: "fa fa-sun-o",
          style: {
            "--ambient-size": "30px",
            "--ambient-delay": "0s",
            "--ambient-duration": "16s",
            "--ambient-angle": "-18deg",
            "--ambient-radius": "116px",
          },
        },
        {
          key: "fish",
          orbitClass: "upper-right",
          colorClass: "fish",
          iconClass: "fa fa-compass",
          style: {
            "--ambient-size": "40px",
            "--ambient-delay": "-4s",
            "--ambient-duration": "27s",
            "--ambient-angle": "30deg",
            "--ambient-radius": "150px",
          },
        },
        {
          key: "peanut",
          orbitClass: "upper-far-left",
          colorClass: "nuts",
          iconClass: "fa fa-circle",
          style: {
            "--ambient-size": "28px",
            "--ambient-delay": "-6.5s",
            "--ambient-duration": "26s",
            "--ambient-angle": "138deg",
            "--ambient-radius": "136px",
          },
        },
        {
          key: "shellfish",
          orbitClass: "mid-left",
          colorClass: "shellfish",
          iconClass: "fa fa-life-ring",
          style: {
            "--ambient-size": "44px",
            "--ambient-delay": "-8s",
            "--ambient-duration": "30s",
            "--ambient-angle": "188deg",
            "--ambient-radius": "162px",
          },
        },
        {
          key: "mustard",
          orbitClass: "mid-right",
          colorClass: "mustard",
          iconClass: "fa fa-dot-circle-o",
          style: {
            "--ambient-size": "32px",
            "--ambient-delay": "-3s",
            "--ambient-duration": "14s",
            "--ambient-angle": "222deg",
            "--ambient-radius": "104px",
          },
        },
        {
          key: "lupin",
          orbitClass: "mid-far-right",
          colorClass: "soy",
          iconClass: "fa fa-pagelines",
          style: {
            "--ambient-size": "30px",
            "--ambient-delay": "-11s",
            "--ambient-duration": "24s",
            "--ambient-angle": "320deg",
            "--ambient-radius": "132px",
          },
        },
        {
          key: "celery",
          orbitClass: "lower-left",
          colorClass: "celery",
          iconClass: "fa fa-envira",
          style: {
            "--ambient-size": "38px",
            "--ambient-delay": "-9s",
            "--ambient-duration": "22s",
            "--ambient-angle": "104deg",
            "--ambient-radius": "138px",
          },
        },
        {
          key: "oat",
          orbitClass: "lower-far-right",
          colorClass: "gluten",
          iconClass: "fa fa-leaf",
          style: {
            "--ambient-size": "28px",
            "--ambient-delay": "-8.4s",
            "--ambient-duration": "20s",
            "--ambient-angle": "248deg",
            "--ambient-radius": "128px",
          },
        },
      ];
    },

    matrixTiles(): MatrixTile[] {
      const columns = 40;
      const rows = 14;
      const tiles: MatrixTile[] = [];
      const rowCenter = (rows - 1) / 2;

      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const waveOffset = Math.abs(row - rowCenter) * 0.007;
          const delay = (column * 0.013) + waveOffset;

          tiles.push({
            key: `tile-${row}-${column}`,
            style: {
              "--tile-col": String(column + 1),
              "--tile-row": String(row + 1),
              "--tile-delay": `${delay}s`,
              "--tile-depth": `${0.86 + (Math.abs(row - rowCenter) / rows) * 0.4}`,
            },
          });
        }
      }

      return tiles;
    },

    matrixThreads(): MatrixThread[] {
      return Array.from({ length: 11 }, (_, index) => ({
        key: `thread-${index}`,
        style: {
          "--thread-top": `${10 + (index * 7.4)}%`,
          "--thread-left": `${-12 + ((index % 3) * 2)}%`,
          "--thread-width": `${74 + ((index % 4) * 6)}%`,
          "--thread-delay": `${index * 0.05}s`,
          "--thread-duration": `${0.94 + (index * 0.04)}s`,
          "--thread-rotate": `${(index - 5) * 3.1}deg`,
          "--thread-scale": `${0.92 + (index * 0.03)}`,
          "--thread-opacity": `${0.44 + ((index % 5) * 0.08)}`,
        },
      }));
    },

    matrixNodes(): MatrixNode[] {
      return [
        {
          key: "node-1",
          style: {
            "--node-top": "16%",
            "--node-left": "12%",
            "--node-size": "13px",
            "--node-delay": "0.08s",
            "--node-duration": "1.62s",
          },
        },
        {
          key: "node-2",
          style: {
            "--node-top": "24%",
            "--node-left": "22%",
            "--node-size": "10px",
            "--node-delay": "0.18s",
            "--node-duration": "1.28s",
          },
        },
        {
          key: "node-3",
          style: {
            "--node-top": "34%",
            "--node-left": "31%",
            "--node-size": "12px",
            "--node-delay": "0.26s",
            "--node-duration": "1.4s",
          },
        },
        {
          key: "node-4",
          style: {
            "--node-top": "28%",
            "--node-left": "43%",
            "--node-size": "15px",
            "--node-delay": "0.34s",
            "--node-duration": "1.76s",
          },
        },
        {
          key: "node-5",
          style: {
            "--node-top": "22%",
            "--node-left": "58%",
            "--node-size": "11px",
            "--node-delay": "0.42s",
            "--node-duration": "1.36s",
          },
        },
        {
          key: "node-6",
          style: {
            "--node-top": "36%",
            "--node-left": "67%",
            "--node-size": "12px",
            "--node-delay": "0.5s",
            "--node-duration": "1.48s",
          },
        },
        {
          key: "node-7",
          style: {
            "--node-top": "48%",
            "--node-left": "52%",
            "--node-size": "16px",
            "--node-delay": "0.58s",
            "--node-duration": "1.92s",
          },
        },
        {
          key: "node-8",
          style: {
            "--node-top": "44%",
            "--node-left": "76%",
            "--node-size": "10px",
            "--node-delay": "0.66s",
            "--node-duration": "1.28s",
          },
        },
        {
          key: "node-9",
          style: {
            "--node-top": "58%",
            "--node-left": "18%",
            "--node-size": "12px",
            "--node-delay": "0.5s",
            "--node-duration": "1.4s",
          },
        },
        {
          key: "node-10",
          style: {
            "--node-top": "64%",
            "--node-left": "33%",
            "--node-size": "11px",
            "--node-delay": "0.68s",
            "--node-duration": "1.34s",
          },
        },
        {
          key: "node-11",
          style: {
            "--node-top": "68%",
            "--node-left": "58%",
            "--node-size": "14px",
            "--node-delay": "0.8s",
            "--node-duration": "1.64s",
          },
        },
        {
          key: "node-12",
          style: {
            "--node-top": "74%",
            "--node-left": "74%",
            "--node-size": "10px",
            "--node-delay": "0.9s",
            "--node-duration": "1.26s",
          },
        },
      ];
    },
  },
});
